import { useEffect, useState } from "react";

/** Route names the console knows about. Placeholder features are still named so the UI can label them. */
export type RouteName =
  | "dashboard"
  | "sites"
  | "site"
  | "vehicles"
  | "vehicle"
  | "users"
  | "map"
  | "alarms"
  | "alarm"
  | "devices"
  | "inventory"
  | "invite"
  | "operators"
  | "operator"
  | "cards"
  | "permissions"
  | "reportUsage"
  | "reportEvents"
  | "reportRoutes"
  | "reportChecklist"
  | "reportHeatmap"
  | "audit"
  | "settings"
  | "checklistFaults"
  | "checklistTemplates"
  | "checklistForm"
  | "profile"
  | "notFound";

export type Route = {
  name: RouteName;
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
};

const PATTERNS: Array<[RouteName, string]> = [
  ["dashboard", "/"],
  ["sites", "/sites"],
  ["site", "/sites/:id"],
  ["vehicles", "/vehicles"],
  ["vehicle", "/vehicles/:id"],
  ["vehicle", "/vehicles/:id/:tab"],
  ["users", "/users"],
  ["map", "/map"],
  ["alarms", "/alarms"],
  ["alarm", "/alarms/:id"],
  ["devices", "/devices"],
  ["inventory", "/inventory"],
  ["invite", "/invite/:token"],
  ["operators", "/operators"],
  ["operator", "/operators/:id"],
  ["cards", "/cards"],
  ["permissions", "/permissions"],
  ["reportUsage", "/reports/usage"],
  ["reportEvents", "/reports/events"],
  ["reportRoutes", "/reports/routes"],
  ["reportChecklist", "/reports/checklist"],
  ["reportHeatmap", "/reports/heatmap"],
  ["audit", "/audit"],
  ["settings", "/settings"],
  // Own account and notification devices (ADR 0019); every role.
  ["profile", "/profile"],
  ["checklistFaults", "/checklists/faults"],
  ["checklistTemplates", "/checklists/templates"],
  // Public pre-shift checklist opened from the QR label (no console login; ADR 0016).
  ["checklistForm", "/c/:token"],
];

function splitPath(p: string): string[] {
  return p.split("/").filter(Boolean);
}

/** Match a path pattern like "/sites/:id" against a concrete path. */
export function matchPattern(pattern: string, path: string): Record<string, string> | null {
  const ps = splitPath(pattern);
  const xs = splitPath(path);
  if (ps.length !== xs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i]!;
    const x = xs[i]!;
    if (p.startsWith(":")) {
      try {
        params[p.slice(1)] = decodeURIComponent(x);
      } catch {
        return null;
      }
    } else if (p !== x) return null;
  }
  return params;
}

/** Parse a location hash ("#/sites/abc?x=1") into a Route. */
export function parseHash(hash: string): Route {
  let raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw.startsWith("/")) raw = "/" + raw;
  const qIdx = raw.indexOf("?");
  const pathPart = qIdx >= 0 ? raw.slice(0, qIdx) : raw;
  const query = new URLSearchParams(qIdx >= 0 ? raw.slice(qIdx + 1) : "");
  const path = pathPart.length > 1 ? pathPart.replace(/\/+$/, "") : "/";
  for (const [name, pattern] of PATTERNS) {
    const params = matchPattern(pattern, path);
    if (params) return { name, path, params, query };
  }
  return { name: "notFound", path, params: {}, query };
}

/** Build an href for an in-app path. */
export function href(path: string, query?: Record<string, string | undefined>): string {
  const qs = query
    ? new URLSearchParams(Object.entries(query).filter((e): e is [string, string] => !!e[1])).toString()
    : "";
  return "#" + path + (qs ? "?" + qs : "");
}

export function navigate(path: string, query?: Record<string, string | undefined>, opts: { replace?: boolean } = {}): void {
  if (opts.replace) {
    // Replace the current history entry (e.g. drop a one-time invite token from the back stack).
    window.history.replaceState(null, "", window.location.pathname + window.location.search + href(path, query));
    window.dispatchEvent(new HashChangeEvent("hashchange"));
    return;
  }
  window.location.hash = href(path, query).slice(1);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseHash(window.location.hash));
  useEffect(() => {
    const onChange = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", onChange);
    onChange();
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}
